import { useMemo } from 'react'

import { MANAGED_CONNECTIONS } from '@/config/managedConnections'

import type {
	Connection,
	ConnectionPlatform,
	ConnectionStatus,
} from 'honeygrid-types/validators-and-types/ConnectionTypes'
import type { ConnectionRowPresentation } from '@/components/Settings/Connections/types'

/**
 * Custom hook that transforms API connections and managed connections
 * into a unified list for display in the Connections UI.
 *
 * Features:
 * - Merges managed connections with API connections
 * - Shows all connection statuses (active, pending, error, expired, inactive)
 * - Handles multiple connections per platform
 * - Adds custom API connections that don't match managed ones
 *
 * @param apiConnections - Connections fetched from the API
 * @returns Array of ConnectionRowPresentation objects ready for display
 */
export function useConnectionRows(
	apiConnections: Connection[] | undefined
): ConnectionRowPresentation[] {
	return useMemo((): ConnectionRowPresentation[] => {
		const allApiConnections = apiConnections || []

		// Group API connections by platform (supports multiple connections per platform)
		const apiConnectionsByPlatform = new Map<ConnectionPlatform, Connection[]>()
		allApiConnections.forEach((conn) => {
			const existing = apiConnectionsByPlatform.get(conn.platform) || []
			apiConnectionsByPlatform.set(conn.platform, [...existing, conn])
		})

		const rows: ConnectionRowPresentation[] = []

		// Process managed connections
		MANAGED_CONNECTIONS.forEach((managed) => {
			// Check if there are API connections for this managed platform
			const platformConnections = managed.platform
				? apiConnectionsByPlatform.get(managed.platform)
				: undefined

			if (platformConnections && platformConnections.length > 0) {
				// Add all connections for this platform
				platformConnections.forEach((apiConnection) => {
					rows.push({
						id: apiConnection.uuid,
						name: apiConnection.name,
						platform: managed.displayName,
						description: managed.description,
						logoSrc: managed.logoSrc,
						owner: 'user' as const,
						status: apiConnection.status,
						managed: false,
						connection: apiConnection,
					})
				})
			} else {
				// Unconnected managed platform
				rows.push({
					id: managed.id,
					name: managed.displayName,
					platform: managed.displayName,
					description: managed.description,
					logoSrc: managed.logoSrc,
					owner: 'workspace' as const,
					status: 'pending' as ConnectionStatus,
					managed: true,
				})
			}
		})

		// Add custom API connections that don't match any managed platform
		allApiConnections.forEach((apiConnection) => {
			const isManaged = MANAGED_CONNECTIONS.some((m) => m.platform === apiConnection.platform)

			if (!isManaged) {
				rows.push({
					id: apiConnection.uuid,
					name: apiConnection.name,
					platform: apiConnection.platform.replace('_', ' '),
					description: undefined,
					logoSrc: '', // No logo for custom connections
					owner: 'user' as const,
					status: apiConnection.status,
					managed: false,
					connection: apiConnection,
				})
			}
		})

		return rows
	}, [apiConnections])
}